import Bugsnag from '@bugsnag/js'
import { createDialog } from '@edge/spark/feature-dialog'
import {
    MutationResolvers,
    PresignedS3UrlDocument,
    PresignedS3UrlQuery,
    CreateDocumentsClientDocument,
    CreateDocumentsClientMutation,
    DocumentListDocument,
    AssetByStockNumberDocument,
    AssetByStockNumberQuery,
    AssetFromAssetIdDocument,
    AssetFromAssetIdQuery,
    GetAuctionIdDocument,
    GetAuctionIdQuery,
    LocalCreateDocumentInput,
} from '../../../../../generated/graphql'
import { convertFileToPdf } from '@edge/spark/io-docs'

import { userAgentParser } from '@edge/spark/utils'

const uploadDocument: MutationResolvers['uploadDocument'] = async (
    _root,
    { input: { file, docTypeShortCode, assetId, stockNumber } },
    { cache, client },
) => {
    if (!client) {
        throw new Error('uploadDocument: Missing ApolloClient')
    }

    const os = userAgentParser.getOS().name

    if (os !== 'Windows') {
        Bugsnag.notify(new Error('uploadDocument'), (event) => {
            event.severity = 'info'
            event.addMetadata('docs', { input: { file, docTypeShortCode, assetId, stockNumber } })
        })
    }

    try {
        let confirmedAccountId = 0
        let confirmedAssetId = 0

        if (!Number.isInteger(assetId) && !Number.isInteger(stockNumber)) {
            throw new Error('uploadDocument: Missing assetId or stockNumber')
        }

        if (Number.isInteger(assetId) && Number.isInteger(stockNumber)) {
            throw new Error('uploadDocument: Used both assetId and stockNumber')
        }

        if (!(file instanceof File) && !(file instanceof Blob)) {
            throw new Error('uploadDocument: Bad File')
        }

        const cacheData = cache.readQuery<GetAuctionIdQuery>({
            query: GetAuctionIdDocument,
        })
        const auctionId = cacheData?.activeOrganization?.orgId

        if (!auctionId) {
            throw new Error('uploadDocument: Missing auctionId')
        }

        const convertedFileToPdf = file.type === 'application/pdf' ? file : await convertFileToPdf(file)
        if (!convertedFileToPdf) {
            throw new Error('Converted File Error')
        }

        if (assetId) {
            const { data: vehicleData } = await client.query<AssetFromAssetIdQuery>({
                query: AssetFromAssetIdDocument,
                variables: { assetId },
            })

            const accountIdFromAssetId = vehicleData.auction?.assets?.byId?.consignor?.accountId

            if (!accountIdFromAssetId) {
                throw new Error('uploadDocument: Missing consignor accountId from assetId')
            }

            confirmedAccountId = accountIdFromAssetId
            confirmedAssetId = Number(assetId)
        } else if (stockNumber) {
            const { data: vehicleData } = await client.query<AssetByStockNumberQuery>({
                query: AssetByStockNumberDocument,
                variables: { stockNumber },
            })

            const dataFromStockNumber = vehicleData.auction?.assets?.byStockNumber

            if (!dataFromStockNumber) {
                throw new Error(`uploadDocument: Missing vehicle data from stockNumber: ${stockNumber}`)
            }

            const accountIdFromStockNumber = vehicleData.auction?.assets?.byStockNumber?.consignorAccountId

            if (!accountIdFromStockNumber) {
                throw new Error('uploadDocument: Missing consignor accountId from stockNumber')
            }

            const assetIdFromStockNumber = vehicleData.auction?.assets?.byStockNumber?.assetId

            if (!assetIdFromStockNumber) {
                throw new Error('uploadDocument: Missing assetId from stockNumber')
            }

            confirmedAccountId = accountIdFromStockNumber
            confirmedAssetId = Number(assetIdFromStockNumber)
        }

        if (!confirmedAccountId) {
            throw new Error('uploadDocument: Missing accountId')
        }

        if (!confirmedAssetId) {
            throw new Error('uploadDocument: Missing assetId')
        }

        const { data: presignedS3Data } = await client.query<PresignedS3UrlQuery>({
            query: PresignedS3UrlDocument,
            variables: { accountId: confirmedAccountId },
            fetchPolicy: 'no-cache',
        })

        const { s3uploadUrl, s3Key, fileName } = presignedS3Data.auction?.document?.documentUploadURLs?.[0] || {}

        if (!s3uploadUrl || !s3Key || !fileName) {
            throw new Error('uploadDocument Missing s3uploadUrl')
        }

        await fetch(s3uploadUrl, {
            method: 'PUT',
            body: convertedFileToPdf,
        }).then((res) => {
            if (!res.ok) {
                throw new Error(`uploadDocument s3 failure ${res.status} ${res.statusText}`)
            }
            return res
        })

        const docsInput: LocalCreateDocumentInput[] = [
            {
                assetId: `${confirmedAssetId}`,
                fileName,
                s3Key,
                docTypeShortCode,
                customerId: `${confirmedAccountId}`,
                stockNumber: `${stockNumber}`,
            },
        ]

        await client.mutate<CreateDocumentsClientMutation>({
            mutation: CreateDocumentsClientDocument,
            variables: { docs: docsInput },
            refetchQueries: [
                {
                    query: DocumentListDocument,
                    variables: { assetId: confirmedAssetId },
                },
            ],
        })

        return true
    } catch (error) {
        console.error(error)
        const message =
            (error instanceof Error && error.message) ||
            (typeof error === 'string' && error) ||
            'Unknown error uploading documents'

        createDialog({
            heading: 'Failed',
            message,
            type: 'ALERT',
        })

        return false
    }
}

export default uploadDocument
